import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import ScoreDashboard from '../../components/dashboard/ScoreDashboard';
import Recommendations from '../../components/dashboard/Recommendations';
import DetailedDataForm from '../../components/dashboard/DetailedDataForm';
import ScrollProgressBar from '../../components/interactive/ScrollProgressBar';
import type { BasicHealthData, DetailedHealthData, HealthScore, UserHealthProfile } from '../../types/health';
import { calculateHealthScore, generateRecommendations, calculateDataCompleteness } from '../../utils/healthScoring';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserHealthProfile | null>(null);
  const [score, setScore] = useState<HealthScore | null>(null);
  const [showAddDataForm, setShowAddDataForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'recommendations' | 'data'>('overview');

  useEffect(() => {
    const storedProfile = localStorage.getItem('healthProfile');
    if (!storedProfile) {
      navigate('/onboarding');
      return;
    }

    try {
      const profileData = JSON.parse(storedProfile);
      
      // Validate that basicData exists and has required fields
      if (!profileData.basicData || !profileData.basicData.smokingStatus) {
        console.error('Invalid profile data structure');
        localStorage.removeItem('healthProfile');
        navigate('/onboarding');
        return;
      }

      const calculatedScore = calculateHealthScore(profileData.basicData, profileData.detailedData);
      const dataCompleteness = calculateDataCompleteness(profileData.basicData, profileData.detailedData);

      const fullProfile: UserHealthProfile = {
        userId: 'user-1',
        basicData: profileData.basicData,
        detailedData: profileData.detailedData || {},
        currentScore: calculatedScore,
        scoreHistory: profileData.scoreHistory || [{ date: new Date(), score: calculatedScore.overall, change: 0 }],
        dataCompleteness,
        createdAt: new Date(profileData.createdAt),
        updatedAt: new Date()
      };

      setProfile(fullProfile);
      setScore(calculatedScore);
    } catch (error) {
      console.error('Error loading health profile:', error);
      localStorage.removeItem('healthProfile');
      navigate('/onboarding');
    }
  }, [navigate]);

  const handleAddData = (category: any) => {
    setSelectedCategory(category);
    setShowAddDataForm(true);
  };

  const handleSaveData = (newData: Partial<DetailedHealthData>) => {
    if (!profile) return;

    const updatedDetailedData = { ...profile.detailedData, ...newData };
    const updatedScore = calculateHealthScore(profile.basicData, updatedDetailedData);
    const dataCompleteness = calculateDataCompleteness(profile.basicData, updatedDetailedData);

    const scoreChange = updatedScore.overall - profile.currentScore.overall;
    const updatedHistory = [
      ...profile.scoreHistory,
      { date: new Date(), score: updatedScore.overall, change: scoreChange }
    ];

    const updatedProfile: UserHealthProfile = {
      ...profile,
      detailedData: updatedDetailedData,
      currentScore: updatedScore,
      scoreHistory: updatedHistory,
      dataCompleteness,
      updatedAt: new Date()
    };

    localStorage.setItem('healthProfile', JSON.stringify({
      basicData: updatedProfile.basicData,
      detailedData: updatedProfile.detailedData,
      scoreHistory: updatedHistory,
      createdAt: profile.createdAt.toISOString()
    }));

    setProfile(updatedProfile);
    setScore(updatedScore);
    setShowAddDataForm(false);
    setSelectedCategory(null);
  };

  const handleResetProfile = () => {
    if (window.confirm('Are you sure you want to reset your health profile? This action cannot be undone.')) {
      localStorage.removeItem('healthProfile');
      navigate('/onboarding');
    }
  };

  if (!profile || !score) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your health profile...</p>
        </div>
      </div>
    );
  }

  if (showAddDataForm && selectedCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
        <TopNavigation />
        <div className="container mx-auto px-4 py-8 pt-24">
          <DetailedDataForm
            category={selectedCategory}
            existingData={profile.detailedData}
            onSave={handleSaveData}
            onCancel={() => {
              setShowAddDataForm(false);
              setSelectedCategory(null);
            }}
          />
        </div>
      </div>
    );
  }

  const recommendations = generateRecommendations(score, profile.basicData, profile.detailedData);

  const dataCategories = [
    { key: 'nutrition', label: 'Nutrition', icon: 'ri-restaurant-line', completed: !!profile.detailedData.nutrition },
    { key: 'exercise', label: 'Exercise', icon: 'ri-run-line', completed: !!profile.detailedData.exercise },
    { key: 'sleep', label: 'Sleep', icon: 'ri-moon-line', completed: !!profile.detailedData.sleep },
    { key: 'stress', label: 'Stress', icon: 'ri-mental-health-line', completed: !!profile.detailedData.stress },
    { key: 'labValues', label: 'Lab Values', icon: 'ri-test-tube-line', completed: !!profile.detailedData.labValues }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
      <ScrollProgressBar color="#14b8a6" height={4} />
      <TopNavigation />

      <div className="container mx-auto px-4 py-8 pt-24">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 bg-white rounded-lg p-1 border-2 border-gray-200 max-w-md">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'overview'
                ? 'bg-teal-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('recommendations')}
            className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'recommendations'
                ? 'bg-teal-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Tips
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'data'
                ? 'bg-teal-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Add Data
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <ScoreDashboard score={score} dataCompleteness={profile.dataCompleteness} />
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div className="max-w-4xl">
            <Recommendations recommendations={recommendations} />
          </div>
        )}

        {/* Add Data Tab */}
        {activeTab === 'data' && (
          <div className="max-w-4xl">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Enhance Your Profile</h2>
              <p className="text-gray-600">Add more detailed health data to improve your score accuracy and get better insights.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {dataCategories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => handleAddData(category.key)}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-300 transition-all text-left group cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors">
                      <i className={`${category.icon} text-2xl text-teal-600`}></i>
                    </div>
                    {category.completed ? (
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full">
                        <i className="ri-check-line text-green-600"></i>
                        <span className="text-xs font-semibold text-green-700">Added</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
                        <span className="text-xs font-semibold text-gray-600">Not Added</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{category.label}</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {category.completed ? 'Update your information' : 'Add detailed information'}
                  </p>
                  <div className="flex items-center text-teal-600 font-semibold text-sm">
                    {category.completed ? 'Edit Data' : 'Add Data'}
                    <i className="ri-arrow-right-line ml-2"></i>
                  </div>
                </button>
              ))}
            </div>

            {/* Score History */}
            {profile.scoreHistory.length > 1 && (
              <div className="mt-8 bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Score History</h3>
                <div className="space-y-4">
                  {profile.scoreHistory.slice().reverse().map((entry, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {new Date(entry.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </div>
                        <div className="text-sm text-gray-600">
                          {new Date(entry.date).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-2xl font-bold text-gray-900">{entry.score}</div>
                        {entry.change !== 0 && (
                          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                            entry.change > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            <i className={`${entry.change > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>
                            <span className="font-semibold">{Math.abs(entry.change)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reset Profile Button */}
            <div className="mt-8 bg-white border-2 border-red-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Reset Profile</h3>
              <p className="text-gray-600 mb-4">
                Want to start over? This will delete all your health data and take you back to the beginning.
              </p>
              <button
                onClick={handleResetProfile}
                className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Reset & Start Over
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
